import { Stack, Typography } from "@mui/material";
import KeyboardArrowRightIcon from "@mui/icons-material/KeyboardArrowRight";
import { FC, memo } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { text, black, divider, background } from "@/theme/colors";
import { spacing } from "@/theme/variables";
import { AppRoutes } from "@/routes/config";
import { TMockExamListItem } from "@/infrastructure/adapters/mock-exam/mock-exam.types";

type TMockExamListItemProps = {
  item: TMockExamListItem;
};

/**
 * A single mock exam card in the list, rendered as a router Link to the detail page.
 * Shows the exam name, its time limit / passing line, and the learner's most
 * recent result as an achievement grade (前回の試験:【B判定】65%) or 未実施.
 */
const MockExamListItem: FC<TMockExamListItemProps> = memo(({ item }) => {
  const { t } = useTranslation();
  const { lastAttempt } = item;

  const lastResultText = t("mockExam.list.lastResult", {
    result: lastAttempt
      ? `${t("mockExam.grade.label", { grade: lastAttempt.grade })}${lastAttempt.scorePercentage}%`
      : t("mockExam.list.notAttempted"),
  });

  return (
    <Stack
      component={Link}
      to={AppRoutes.mockExamDetail.replace(":mockExamId", String(item.mockExamId))}
      direction="row"
      alignItems="center"
      gap={spacing.smd}
      px={spacing.smd}
      py={spacing.xs}
      bgcolor={background.white}
      border={`1px solid ${divider.middle}`}
    >
      {/* Left column: name, meta, last result */}
      <Stack flexGrow={1} minWidth={0} gap={spacing.xs}>
        <Stack>
          <Typography variant="body14Bold" color={text.brand}>
            {item.name}
          </Typography>
          <Typography variant="cap12" color={text.secondary}>
            {t("mockExam.list.meta", { minutes: item.timeLimitMinutes, percent: item.passingScorePercentage })}
          </Typography>
        </Stack>

        <Stack direction="row" justifyContent="space-between" alignItems="center" gap={spacing.xs}>
          <Typography variant="cap12" color={text.primary} flexGrow={1} minWidth={0}>
            {lastResultText}
          </Typography>
          {lastAttempt && (
            <Typography variant="cap12" color={text.secondary} noWrap>
              {lastAttempt.date}
            </Typography>
          )}
        </Stack>
      </Stack>

      {/* Right: chevron */}
      <KeyboardArrowRightIcon htmlColor={black[80]} sx={{ flexShrink: 0 }} />
    </Stack>
  );
});

MockExamListItem.displayName = "MockExamListItem";

export { MockExamListItem };
